import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Plus, Search, Printer, Trash2 } from "lucide-react";
import { api, errCode, download } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth, isStaff } from "../lib/auth";
import { useMaster, nameOf } from "../lib/master";
import { PageTitle, Sel, Inp } from "../components/Common";
import { LabelPreviewButton, usePrintJob, PrintProgress, PrintHistory } from "../components/LabelPrint";

const useErr = () => { const { te } = useLang(); return (e) => toast.error(te(errCode(e))); };

const Batches = () => {
  const { t } = useLang();
  const onErr = useErr();
  const growers = useMaster("growers");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(500);
  const [busy, setBusy] = useState("");
  const [sel, setSel] = useState([]);
  const { start, prog } = usePrintJob();
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selCount = rows.filter((b) => sel.includes(b.id)).reduce((a, b) => a + b.count, 0);
  const load = () => api.get("/label-batches").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = () => api.post("/label-batches", { count: Number(count), is_test: false }).then(() => { toast.success(t("saved")); load(); }).catch(onErr);
  const allocate = (id, grower_id) => grower_id && api.post(`/label-batches/${id}/allocate`, { grower_id }).then(() => { toast.success(t("saved")); load(); }).catch(onErr);
  const del = (b) => {
    const used = (b.status_counts || {}).active || 0;
    if (!window.confirm(t("batch_delete_confirm").replace("{n}", b.count - used).replace("{u}", used).replace("{b}", b.batch_no))) return;
    api.delete(`/label-batches/${b.id}`).then((r) => { toast.success(`${t("deleted")}: ${r.data.deleted}${r.data.kept ? ` · ${t("batch_kept")}: ${r.data.kept}` : ""}`); setSel((x) => x.filter((i) => i !== b.id)); load(); }).catch(onErr);
  };
  const pdf = async (b) => { setBusy(b.id); try { await download(`/label-batches/${b.id}/pdf`, `${b.batch_no}.pdf`); } catch (e) { onErr(e); } setBusy(""); };
  return (
    <section className="card" data-testid="batches-section">
      <div className="p-4 flex flex-wrap gap-3 items-end border-b">
        <h2 className="font-bold text-lg mr-auto">{t("batch")}</h2>
        <Inp label={t("count")} type="number" min={1} max={1000} value={count} onChange={(e) => setCount(e.target.value)} testid="batch-count-input" />
        <button className="btn-primary" onClick={create} data-testid="create-batch-button"><Plus size={18} />{t("new_batch")}</button>
      </div>
      {sel.length > 0 && <div className="p-3 bg-rose-50 border-b flex flex-wrap items-center gap-3" data-testid="batch-selection-bar">
        <span className="text-sm font-bold">{sel.length} {t("batch").toLowerCase()} · {selCount.toLocaleString("es-CL")} {t("nav_labels").toLowerCase()}</span>
        <button className="btn-primary !min-h-[44px]" disabled={!!prog} onClick={async () => { if (await start(sel)) setSel([]); }} data-testid="print-selected-button"><Printer size={18} />{t("print_selected")}</button>
        <button className="text-sm underline" onClick={() => setSel([])}>{t("cancel")}</button>
        <div className="w-full"><PrintProgress prog={prog} /></div>
      </div>}
      <div className="overflow-x-auto"><table className="tbl w-full" data-testid="batches-table">
        <thead><tr><th></th><th>{t("batch")}</th><th>{t("range")}</th><th>{t("grower")}</th><th>{t("status")}</th><th></th></tr></thead>
        <tbody>{rows.map((b) => (
          <tr key={b.id}>
            <td><input type="checkbox" className="w-5 h-5" checked={sel.includes(b.id)} onChange={() => toggle(b.id)} data-testid={`batch-select-${b.batch_no}`} /></td>
            <td className="font-mono font-bold">{b.batch_no}</td>
            <td className="font-mono text-xs">CH-…{String(b.start_seq).padStart(6, "0")} → {String(b.end_seq).padStart(6, "0")} ({b.count})</td>
            <td>{b.grower_id ? nameOf(growers, b.grower_id) : (
              <select className="field !min-h-[40px]" defaultValue="" onChange={(e) => allocate(b.id, e.target.value)} data-testid={`allocate-select-${b.batch_no}`}>
                <option value="">{t("allocate")}…</option>{growers.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>)}</td>
            <td className="text-xs">{Object.entries(b.status_counts || {}).map(([k, v]) => `${t(`lst_${k}`)}: ${v}`).join(" · ")}</td>
            <td className="flex gap-2"><LabelPreviewButton small batchId={b.id} testid={`batch-preview-${b.batch_no}`} /><button className="btn-secondary btn-sm" disabled={busy === b.id} onClick={() => pdf(b)} data-testid={`batch-pdf-${b.batch_no}`}><FileDown size={16} />{busy === b.id ? "…" : t("download_pdf")}</button>
              <button className="btn-secondary btn-sm !text-red-700" onClick={() => del(b)} aria-label={t("delete")} data-testid={`batch-delete-${b.batch_no}`}><Trash2 size={16} /></button></td>
          </tr>))}</tbody></table></div>
    </section>
  );
};

const Bulk = ({ onDone }) => {
  const { t } = useLang();
  const onErr = useErr();
  const growers = useMaster("growers");
  const farms = useMaster("farms");
  const csgs = useMaster("csgs");
  const [f, setF] = useState({ count: 1000, batch_size: 500, grower_id: "", farm_id: "" });
  const [creating, setCreating] = useState(false);
  const { start, prog } = usePrintJob();
  const farmCsgs = csgs.filter((c) => c.farm_id === f.farm_id);
  const run = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/label-batches/bulk", { count: Number(f.count), batch_size: Number(f.batch_size), grower_id: f.grower_id || null, farm_id: f.farm_id || null, is_test: false });
      onDone();
      setCreating(false);
      if (await start(data.batches.map((b) => b.id))) toast.success(`${data.labels.toLocaleString("es-CL")} ${t("nav_labels").toLowerCase()} · 1 PDF`);
    } catch (e) { onErr(e); }
    setCreating(false);
  };
  const busy = creating || !!prog;
  return (
    <section className="card p-4 space-y-3 border-2 border-[#C8102E]" data-testid="bulk-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-bold text-lg flex items-center gap-2"><Printer size={20} />{t("bulk_title")}</h2>
        <LabelPreviewButton testid="bulk-preview-button" />
      </div>
      <p className="text-sm text-slate-600">{t("bulk_hint")}</p>
      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
        <Inp label={`${t("count")} (${t("max_10000")})`} type="number" min={1} max={10000} value={f.count} onChange={(e) => setF({ ...f, count: e.target.value })} testid="bulk-count-input" />
        <label><span className="eyebrow">{t("batch_size")}</span>
          <select className="field mt-1" value={f.batch_size} onChange={(e) => setF({ ...f, batch_size: e.target.value })} data-testid="bulk-batch-size-select">
            <option value={500}>500</option><option value={1000}>1000</option></select></label>
        <Sel label={t("allocate")} value={f.grower_id} onChange={(v) => setF({ ...f, grower_id: v, farm_id: "" })} options={growers} placeholder={t("no_grower_opt")} testid="bulk-grower-select" />
        <Sel label={`${t("issue_to_farm")} (${t("optional")})`} value={f.farm_id} onChange={(v) => setF({ ...f, farm_id: v })} disabled={!f.grower_id}
          options={farms.filter((x) => x.grower_id === f.grower_id)} placeholder={f.grower_id ? t("no_farm_opt") : t("pick_grower_first")} testid="bulk-farm-select" />
        <button className="btn-primary" disabled={busy || !(Number(f.count) >= 1 && Number(f.count) <= 10000)} onClick={run} data-testid="bulk-create-print-button">
          <Printer size={20} />{t("bulk_create_print")}</button>
      </div>
      <p className="text-xs font-semibold text-slate-700" data-testid="bulk-prefill-hint">
        {!f.grower_id ? t("prefill_none") : !f.farm_id ? t("prefill_grower") : farmCsgs.length === 1 ? `${t("prefill_grower_csg")} ${farmCsgs[0].code}` : t("prefill_multi_csg")}
      </p>
      {creating && <p className="font-mono text-sm font-bold" data-testid="bulk-progress">{t("creating_labels")}</p>}
      <PrintProgress prog={prog} />
    </section>
  );
};

const Issue = () => {
  const { t } = useLang();
  const onErr = useErr();
  const farms = useMaster("farms");
  const [sups, setSups] = useState([]);
  const [f, setF] = useState({ from_number: "", to_number: "", farm_id: "", supervisor_id: "" });
  useEffect(() => { api.get("/users/supervisors").then((r) => setSups(r.data)); }, []);
  const submit = () => api.post("/labels/issue", { ...f, supervisor_id: f.supervisor_id || null })
    .then((r) => toast.success(`${t("issued_n")}: ${r.data.issued}`)).catch(onErr);
  return (
    <section className="card p-4 space-y-3" data-testid="issue-section">
      <h2 className="font-bold text-lg">{t("issue_labels")}</h2>
      <div className="grid sm:grid-cols-4 gap-3">
        <Inp label={t("from_number")} placeholder="CH-2026-000001" value={f.from_number} onChange={(e) => setF({ ...f, from_number: e.target.value })} testid="issue-from-input" />
        <Inp label={t("to_number")} placeholder="CH-2026-000050" value={f.to_number} onChange={(e) => setF({ ...f, to_number: e.target.value })} testid="issue-to-input" />
        <Sel label={t("farm")} value={f.farm_id} onChange={(v) => setF({ ...f, farm_id: v })} options={farms} testid="issue-farm-select" placeholder={t("select")} />
        <Sel label={`${t("supervisor")} (${t("optional")})`} value={f.supervisor_id} onChange={(v) => setF({ ...f, supervisor_id: v })}
          options={sups.filter((s) => s.farm_ids.includes(f.farm_id)).map((s) => ({ id: s.id, name: s.full_name }))} testid="issue-supervisor-select" />
      </div>
      <button className="btn-primary" disabled={!f.from_number || !f.to_number || !f.farm_id} onClick={submit} data-testid="issue-submit-button">{t("issue")}</button>
    </section>
  );
};

const Finder = () => {
  const { t } = useLang();
  const onErr = useErr();
  const farms = useMaster("farms");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState("");
  const search = () => api.get("/labels", { params: { q } }).then((r) => setRows(r.data.slice(0, 50)));
  const reprint = (l) => download(`/labels/${l.id}/reprint`, `${l.number}.pdf`, { method: "post", data: { reason } }).then(() => toast.success(t("saved"))).catch(onErr);
  const voidIt = (l) => api.post(`/labels/${l.id}/void`, { reason }).then(() => { toast.success(t("saved")); search(); }).catch(onErr);
  return (
    <section className="card p-4 space-y-3" data-testid="finder-section">
      <h2 className="font-bold text-lg">{t("find_label")}</h2>
      <div className="flex flex-wrap gap-2 items-end">
        <input className="field max-w-xs font-mono" placeholder="000123" value={q} onChange={(e) => setQ(e.target.value)} data-testid="label-search-input" />
        <button className="btn-secondary" onClick={search} data-testid="label-search-button"><Search size={18} /></button>
        <input className="field max-w-sm" placeholder={`${t("reason")} (${t("reprint")}/${t("void")})`} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="label-action-reason-input" />
      </div>
      <table className="tbl w-full"><tbody>{rows.map((l) => (
        <tr key={l.id}><td className="font-mono font-bold">{l.number}</td><td>{t(`lst_${l.status}`)}</td><td>{nameOf(farms, l.farm_id)}</td><td className="text-xs">{t("reprint")}: {l.reprint_count}</td>
          <td className="flex gap-2"><button className="btn-secondary btn-sm" disabled={reason.length < 3 || l.status === "void"} hidden={l.status === "void"} onClick={() => reprint(l)} data-testid={`reprint-${l.number}`}>{t("reprint")}</button>
            <button className="btn-secondary btn-sm text-red-800" disabled={reason.length < 3 || ["void", "active"].includes(l.status)} hidden={l.status === "void"} onClick={() => voidIt(l)} data-testid={`void-${l.number}`}>{t("void")}</button></td></tr>
      ))}</tbody></table>
    </section>
  );
};

export default function Labels() {
  const { t } = useLang();
  const { user } = useAuth();
  const [ver, setVer] = useState(0);
  return (
    <div className="space-y-5" data-testid="labels-page">
      <PageTitle>{t("labels_title")}</PageTitle>
      {isStaff(user) && <Bulk onDone={() => setVer((v) => v + 1)} />}
      {isStaff(user) && <Batches key={ver} />}
      {isStaff(user) && <PrintHistory />}
      <Issue />
      <Finder />
    </div>
  );
}
